/**
 * Jyotish Tarang -- language switcher.
 * Phase 1 (14 Jul 2026, LANGUAGE_SUPPORT_PLAN.md Section 7).
 *
 * Shows all 19 languages in the owner-decided display order (Hindi,
 * English, then remaining 17 alphabetically -- see languages.ts, do not
 * re-sort). Only languages listed in the backend's `enabled_languages`
 * config are actually selectable; the rest render disabled with a
 * "(coming soon)" suffix so users can see the full roadmap without being
 * able to pick a language that has no real translated content yet.
 *
 * On change: switches i18next immediately (instant UI update, no reload)
 * and, if the user is logged in, persists the choice to
 * `users.ui_language` via POST /api/auth/language so it survives across
 * devices/sessions rather than only living in this browser's localStorage.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { LANGUAGES } from '../languages';
import { useAuth } from '../App';

export default function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const { user }     = useAuth();
  const [enabled, setEnabled] = useState<string[]>(['en']);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    axios.get('/api/config/tiers')
      .then(r => { if (Array.isArray(r.data?.enabled_languages)) setEnabled(r.data.enabled_languages); })
      .catch(() => {}); // keep the ['en']-only default if the call fails
  }, []);

  // Sync the app's displayed language to the signed-in user's saved
  // preference on load / login (localStorage may hold a stale/guest choice
  // from before they logged in -- the backend value wins once known).
  useEffect(() => {
    const pref = user?.ui_language;
    if (pref && pref !== i18n.language) i18n.changeLanguage(pref);
    // (i18n is a stable module-level instance; only re-run when `user` changes)
  }, [user]); // eslint-disable-line

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    i18n.changeLanguage(code);
    if (user) {
      setSaving(true);
      axios.post('/api/auth/language', { ui_language: code })
        .catch(() => {})
        .finally(() => setSaving(false));
    }
  };

  return (
    <select
      className="jt-lang-switcher"
      aria-label={t('language.switcherLabel')}
      title={t('language.switcherLabel')}
      value={i18n.language}
      disabled={saving}
      onChange={handleChange}
      style={{
        fontSize: 'var(--jt-fs-xs, 11px)',
        fontFamily: 'var(--font-body)',
        color: 'var(--ink)',
        background: 'var(--cream)',
        border: '1px solid var(--parch2)',
        borderRadius: 'var(--radius-sm, 6px)',
        padding: '4px 6px',
        cursor: 'pointer',
      }}
    >
      {LANGUAGES.map(lang => {
        const isEnabled = enabled.includes(lang.code);
        return (
          <option key={lang.code} value={lang.code} disabled={!isEnabled}>
            {lang.nativeName}
            {lang.nativeName !== lang.englishName ? ` (${lang.englishName})` : ''}
            {!isEnabled ? ` — ${t('language.comingSoon')}` : ''}
          </option>
        );
      })}
    </select>
  );
}
